/**
 * Ljud catalog tiers on /produkter?category=Ljud.
 * Business classification is slug/id — product display names must not move tiers.
 */

export type LjudTierId = 'standard' | 'premium' | 'stora_event';

export const LJUD_TIER_STICKY_NAV = false;

export const LJUD_TIER_ORDER: readonly LjudTierId[] = [
  'standard',
  'premium',
  'stora_event',
] as const;

export const LJUD_TIER_COPY: Readonly<
  Record<LjudTierId, { title: string; blurb: string }>
> = {
  standard: {
    title: 'Standard',
    blurb: 'För fest, bröllop, DJ och mindre event.',
  },
  premium: {
    title: 'Premium',
    blurb:
      'För dig som vill ha högre ljudkvalitet, kapacitet och ett mer professionellt system.',
  },
  stora_event: {
    title: 'Stora event & produktion',
    blurb:
      'För större publik, konserter och produktioner. Vi hjälper dig dimensionera rätt ljudsystem.',
  },
};

/** Explicit live mapping (verified 2026-09-17). */
export const LJUD_TIER_BY_SLUG: Readonly<Record<string, LjudTierId>> = {
  // Standard
  '2-x-alto-ts415-5000w': 'standard',
  'soundboks-gen-3-portabel-festhogtalare-med-extremt-tryck': 'standard',
  'mackie-thump-go-batteridrivet-pa-system-2-st': 'standard',
  'lilla-ljudpaketet': 'standard',
  'mellan-ljudpaketet': 'standard',
  'hogtalarpaket-10-000w': 'standard',
  'subwoofer-kraftfull-och-djup-bas': 'standard',
  // Premium
  'premium-rcf-art-912-a-dubbel-inkl-stativ': 'premium',
  'premium-rcf-nxl-24-a-mk2-2-st': 'premium',
  'premium-rcf-nxl-24-a-mk2-sub-18-ax-komplett-ljudsystem': 'premium',
  'premium-rcf-sub-18-ax': 'premium',
  'premium-rcf-art-415-a-dubbel-inkl-stativ': 'premium',
  // Stora event & produktion
  'rcf-hdl-6-a-kompakt-line-array': 'stora_event',
  'rcf-sub-9029-as-professionell-dubbel-19-subwoofer': 'stora_event',
  'd-b-xsl8': 'stora_event',
  'd-b-xsl12': 'stora_event',
  'd-b-sl-gsub': 'stora_event',
};

/** Stable product ids for the same live set (secondary SoT if slug missing). */
export const LJUD_TIER_BY_ID: Readonly<Record<string, LjudTierId>> = {
  'ca4b01b6-96a8-46df-bf27-6ed6a490669e': 'standard',
  'ac0508cf-ecbb-4dec-aba0-1c586838aba4': 'standard',
  '70a2e4b6-97e8-4c8a-b0b1-b102b3148163': 'standard',
  '021473fa-a1fc-4b1d-a562-65570c928330': 'standard',
  'a0ddfba4-43b7-4603-9381-26ce55afac3d': 'standard',
  'd4fc2287-dda1-4a31-9388-c3739ffd1511': 'standard',
  '17a2f5aa-bbfa-44ed-9792-0d852b89606b': 'standard',
  '290ec873-8ddc-4bf0-ae13-c7b2ee684111': 'premium',
  'bb2b151b-acad-45eb-ad5b-73203bf2ff09': 'premium',
  '1128f6ac-a4ee-4c76-8342-005c9bc647be': 'premium',
  '793a4920-63db-4d9e-9708-cd47ec70449e': 'premium',
  '015dfd04-f85a-4448-b775-17e071b9ea37': 'premium',
  'dc91e2d8-15e8-470f-83bc-6d4a7ac37f35': 'stora_event',
  'ac77d4f7-cc12-448c-ac09-7ed14eb62b88': 'stora_event',
  '9ca24946-76c6-49dc-8834-1c5f83a414bc': 'stora_event',
  '1628a4a4-b2d3-4eea-9a63-5ac4e6239c99': 'stora_event',
  '978d8e7c-c7d5-437d-b775-37663f43822a': 'stora_event',
};

export type LjudTierProductRef = {
  id?: string;
  slug?: string | null;
  name?: string;
};

/**
 * Resolve tier. Explicit slug, then id. Name is ignored for mapped products.
 * Unknown products fall back to standard (defensive only).
 */
export function resolveLjudTier(product: LjudTierProductRef): LjudTierId {
  const slug = String(product.slug ?? '')
    .trim()
    .toLowerCase();
  if (slug && LJUD_TIER_BY_SLUG[slug]) {
    return LJUD_TIER_BY_SLUG[slug];
  }
  const id = String(product.id ?? '').trim();
  if (id && LJUD_TIER_BY_ID[id]) {
    return LJUD_TIER_BY_ID[id];
  }
  return 'standard';
}

export type LjudTierGroup<T extends LjudTierProductRef> = {
  tier: LjudTierId;
  title: string;
  blurb: string;
  products: T[];
};

export function groupLjudProducts<T extends LjudTierProductRef>(
  products: readonly T[]
): LjudTierGroup<T>[] {
  const buckets: Record<LjudTierId, T[]> = {
    standard: [],
    premium: [],
    stora_event: [],
  };
  for (const product of products) {
    buckets[resolveLjudTier(product)].push(product);
  }
  return LJUD_TIER_ORDER.map((tier) => ({
    tier,
    title: LJUD_TIER_COPY[tier].title,
    blurb: LJUD_TIER_COPY[tier].blurb,
    products: buckets[tier],
  })).filter((g) => g.products.length > 0);
}
